import { Grid } from "@mui/material";
import { memo, useEffect, useRef } from "react";
import { useFormContext } from "@4uhub/lib4uhub";
import { useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { WhatsappSection } from "../../../components/WhatsappTemplate/WhatsappSection/WhatsappSection";
import { TextField } from "../../../components/WhatsappTemplate/TextField/TextField";
import { BodyField } from "../../../components/WhatsappTemplate/BodyField/BodyField";
import { TWhatsappTemplateForm } from "./whatsappTemplateSchema";
import { IWhatsappBasicDataProps } from "./models";
import { useTemplateWhats } from "../../../store/contexts/TemplateWhatsContext";
import { FileFixedRadio } from "./FileFixedRadio";
import { useParams } from "react-router-dom";
import FormInformation from "../../../components/FormInformation/FormInformation";
import { HeaderType } from "./HeaderType";
import { SelectAutomationRuleInputFile } from "./SelectAutomationRuleInputFile";

const translationPath = "chatBot.page.templates.whatsapp_template.";

export const WhatsappBasicData = memo(
  ({ onDeleteFile }: IWhatsappBasicDataProps) => {
    const { id } = useParams();

    const { t } = useTranslation();

    const { control, setValue, getValues } =
      useFormContext<TWhatsappTemplateForm>();

    const { software } = useTemplateWhats();

    const ticketAutomation = useWatch({ name: "ticketAutomation", control });
    const ticketAutomationInternalTemplate = useWatch({
      name: "ticketAutomationInternalTemplate",
      control,
    });

    const headerType = useWatch({
      name: "headerType",
      control,
    });

    const prevHeaderCodeRef = useRef<string | undefined>(undefined);

    useEffect(() => {
      const newCode = headerType?.code as string | undefined;
      const prevCode = prevHeaderCodeRef.current;
      const mediaFile = getValues("mediaFile");

      if (!newCode) {
        setValue("title", undefined);
        if (mediaFile) {
          onDeleteFile(mediaFile);
        }
        setValue("mediaFile", null);
        prevHeaderCodeRef.current = newCode;
        return;
      }

      if (prevCode && prevCode !== newCode && newCode === "1") {
        if (mediaFile) {
          onDeleteFile(mediaFile);
        }
        setValue("mediaFile", null);
      } else if (
        prevCode &&
        prevCode !== newCode &&
        (newCode === "2" || newCode === "3" || newCode === "4")
      ) {
        if (mediaFile) {
          onDeleteFile(mediaFile);
        }
        setValue("mediaFile", null);
        setValue("title", undefined);

        if (prevCode && prevCode !== newCode) {
          if (mediaFile) {
            onDeleteFile(mediaFile);
          }
          setValue("mediaFile", null);
        }
      }

      prevHeaderCodeRef.current = newCode;
    }, [headerType?.code, getValues, onDeleteFile, setValue]);

    return (
      <>
        <Grid item xs={12} sm={12}>
          <WhatsappSection
            title={t(translationPath + "header")}
            subtitle={t(translationPath + "header_sub")}
            chipLabel={
              id && headerType?.code !== "1"
                ? ""
                : `${t(translationPath + "optional")}`
            }
          >
            <>
              {id && headerType && (
                <FormInformation
                  label={t(translationPath + "header_type")}
                  data={headerType.name}
                />
              )}

              {!id && <HeaderType />}

              {headerType?.code === "1" && (
                <TextField
                  name="title"
                  label={t(translationPath + "title")}
                  maxLength={60}
                />
              )}

              {headerType &&
                (headerType.code === "2" || headerType.code === "4") && (
                  <>
                    <SelectAutomationRuleInputFile
                      onDeleteFile={onDeleteFile}
                    />

                    {software === "MarCom" && <FileFixedRadio id={id} />}

                    {software === "ChatBot" &&
                      ticketAutomation?.code === "8" && (
                        <FileFixedRadio id={id} />
                      )}
                  </>
                )}
            </>
          </WhatsappSection>
        </Grid>

        <BodyField
          automationId={ticketAutomation ? ticketAutomation.id : ""}
          internalTemplateId={
            ticketAutomationInternalTemplate
              ? ticketAutomationInternalTemplate.id
              : ""
          }
          componentType="BODY"
        />

        <Grid item xs={12} sm={12}>
          <WhatsappSection
            title={t(translationPath + "footer")}
            subtitle={t(translationPath + "footer_sub")}
            chipLabel={`${t(translationPath + "optional")}`}
          >
            <TextField
              name="footer"
              label={t(translationPath + "footer")}
              maxLength={60}
            />
          </WhatsappSection>
        </Grid>
      </>
    );
  },
);
